import { Button, Form, Input, Row, Select, Space, Table, Tooltip } from 'antd'
import { createContext, HtmlHTMLAttributes, useContext, useEffect, useReducer, useState } from 'react'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripDotsVertical, faPenToSquare } from '@fortawesome/pro-solid-svg-icons'
import { CuratedTemplate, CuratedTemplateItem } from '../../../domain/curatedTemplates/curatedTemplate'
import { useUpsertCuratedTemplateMutation } from '../../../domain/curatedTemplates/curatedTemplatesQueries'
import { ProductionRate } from '../../../domain/productionRate'
import {
  createEditorStateFrom,
  curatedItemClassOptions,
  curatedTemplateEditorReducer,
  formatProductionRate,
  getSaveDisabledReason,
  isChanged,
  sortedItems,
  toUpsertRequest
} from './curatedTemplateEditorModel'
import { curatedAvailabilityOptions } from './curatedTemplatesDisplayModel'
import ChangeOnBlurInput from '../../common/ChangeOnBlurInput'
import DeleteButton from '../../common/DeleteButton'
import DollarInput from '../../common/DollarInput'
import VSpace from '../../common/VSpace'
import ProductionRateForm from '../../ItemCatalog/ProductionRateForm'

type Props = {
  template?: CuratedTemplate
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void
  onSaved: () => void
  onCancel: () => void
}

type ProductionRateFormState = {
  isOpen: boolean
  curatedTemplateItemId: string
  productionRate?: Partial<ProductionRate>
}

const closedProductionRateForm: ProductionRateFormState = { isOpen: false, curatedTemplateItemId: '' }

const CuratedTemplateEditor = ({ template, onUnsavedChangesChange, onSaved, onCancel }: Props) => {
  const [state, dispatch] = useReducer(curatedTemplateEditorReducer, template, createEditorStateFrom)
  const [productionRateFormState, setProductionRateFormState] = useState(closedProductionRateForm)
  const upsertMutation = useUpsertCuratedTemplateMutation()

  const items = sortedItems(state.draft.items)
  const saveDisabledReason = getSaveDisabledReason(state)
  const hasUnsavedChanges = isChanged(state)

  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  const updateItem = (curatedTemplateItemId: string, changes: Partial<CuratedTemplateItem>) => {
    dispatch({ type: 'ITEM_UPDATED', curatedTemplateItemId, changes })
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return
    }
    dispatch({
      type: 'ITEMS_REORDERED',
      oldIndex: items.findIndex(x => x.curatedTemplateItemId === active.id),
      newIndex: items.findIndex(x => x.curatedTemplateItemId === over.id)
    })
  }

  const editProductionRate = (item: CuratedTemplateItem) => {
    setProductionRateFormState({
      isOpen: true,
      curatedTemplateItemId: item.curatedTemplateItemId,
      productionRate: {
        rateInstallationQuantity: item.rateInstallationQuantity,
        ratePerUnits: item.ratePerUnits,
        rateUsageQuantity: item.rateUsageQuantity,
        units: item.units
      }
    })
  }

  const handleSave = () => {
    upsertMutation.mutate(toUpsertRequest(state.draft), { onSuccess: onSaved })
  }

  return <VSpace size="large">
    <Form layout="vertical">
      <Form.Item label="Name" required>
        <Input
          value={state.draft.name}
          placeholder='Template Name'
          maxLength={150}
          status={state.draft.name.trim() === '' ? 'error' : undefined}
          onChange={e => dispatch({ type: 'NAME_UPDATED', value: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Availability">
        <Select
          style={{ width: 200 }}
          options={curatedAvailabilityOptions}
          value={state.draft.availability}
          onChange={value => dispatch({ type: 'AVAILABILITY_UPDATED', value })}
        />
      </Form.Item>
      <Form.Item label="Description">
        <Input.TextArea
          value={state.draft.description}
          rows={2}
          onChange={e => dispatch({ type: 'DESCRIPTION_UPDATED', value: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="AI Instructions">
        <Input.TextArea
          value={state.draft.aiInstructions}
          rows={4}
          onChange={e => dispatch({ type: 'AI_INSTRUCTIONS_UPDATED', value: e.target.value })}
        />
      </Form.Item>
    </Form>

    <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={items.map(x => x.curatedTemplateItemId)} strategy={verticalListSortingStrategy}>
        <Table<CuratedTemplateItem>
          dataSource={items}
          rowKey="curatedTemplateItemId"
          components={{
            body: { row: SortableRow }
          }}
          columns={[
            {
              key: 'dragHandle',
              width: 40,
              render: () => <DragHandle />
            },
            {
              title: 'Name',
              render: (_, item) => <VSpace size="small">
                <ChangeOnBlurInput
                  value={item.name}
                  placeholder='Name'
                  maxLength={150}
                  highlightWhenEmpty
                  onBlurChange={value => updateItem(item.curatedTemplateItemId, { name: value })}
                />
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  dropdownStyle={{ display: 'none' }}
                  suffixIcon={null}
                  tokenSeparators={[',']}
                  placeholder="Alternate names"
                  value={item.nameAliases}
                  onChange={nameAliases => dispatch({
                    type: 'NAME_ALIASES_UPDATED',
                    curatedTemplateItemId: item.curatedTemplateItemId,
                    nameAliases
                  })}
                />
              </VSpace>
            },
            {
              title: 'Class',
              width: 160,
              render: (_, item) => <Select
                style={{ width: '100%' }}
                options={curatedItemClassOptions}
                value={item.class}
                onChange={value => updateItem(item.curatedTemplateItemId, { class: value })}
              />
            },
            {
              title: 'Units',
              width: 120,
              render: (_, item) => <ChangeOnBlurInput
                value={item.units ?? ''}
                placeholder='Units'
                maxLength={50}
                onBlurChange={value => updateItem(item.curatedTemplateItemId, { units: value })}
              />
            },
            {
              title: 'Cost',
              width: 160,
              render: (_, item) => <DollarInput
                style={{ width: '100%' }}
                min={0}
                placeholder="Not set"
                value={item.cost}
                onChange={value => updateItem(item.curatedTemplateItemId, { cost: value })}
              />
            },
            {
              title: 'Production Rate',
              width: 250,
              render: (_, item) => <Row justify="space-between" align="middle" wrap={false} style={{ flexDirection: 'row-reverse', gap: 8 }}>
                <Button icon={<FontAwesomeIcon icon={faPenToSquare} />} onClick={() => editProductionRate(item)} />
                {formatProductionRate(item)}
              </Row>
            },
            {
              align: 'right',
              width: 50,
              render: (_, item) => <DeleteButton
                onClick={() => dispatch({ type: 'ITEM_REMOVED', curatedTemplateItemId: item.curatedTemplateItemId })}
              />
            }
          ]}
          pagination={false}
        />
      </SortableContext>
    </DndContext>

    <Button onClick={() => dispatch({ type: 'ITEM_ADDED' })}>+ Add Item</Button>

    <Row justify="end">
      <Space>
        <Button onClick={onCancel}>Cancel</Button>
        <Tooltip title={saveDisabledReason}>
          <Space>
            <Button
              type="primary"
              disabled={saveDisabledReason !== ''}
              loading={upsertMutation.isLoading}
              onClick={handleSave}
            >
              Save
            </Button>
          </Space>
        </Tooltip>
      </Space>
    </Row>

    <ProductionRateForm
      isOpen={productionRateFormState.isOpen}
      productionRate={productionRateFormState.productionRate}
      onChange={productionRate => {
        dispatch({
          type: 'PRODUCTION_RATE_UPDATED',
          curatedTemplateItemId: productionRateFormState.curatedTemplateItemId,
          productionRate
        })
        setProductionRateFormState(closedProductionRateForm)
      }}
      onCancel={() => setProductionRateFormState(closedProductionRateForm)}
      onClear={() => {
        dispatch({
          type: 'PRODUCTION_RATE_CLEARED',
          curatedTemplateItemId: productionRateFormState.curatedTemplateItemId
        })
        setProductionRateFormState(closedProductionRateForm)
      }}
    />
  </VSpace>
}

export default CuratedTemplateEditor

type SortableRowProps = HtmlHTMLAttributes<HTMLTableRowElement> & {
  'data-row-key': string
}

type DragActivator = Pick<ReturnType<typeof useSortable>, 'setActivatorNodeRef' | 'attributes' | 'listeners'>

const DragActivatorContext = createContext<Partial<DragActivator>>({})

const SortableRow = (props: SortableRowProps) => {
  const { setNodeRef, setActivatorNodeRef, transform, transition, attributes, listeners } = useSortable({
    id: props['data-row-key']
  })

  const style: React.CSSProperties = {
    ...props.style,
    transition,
    transform: CSS.Transform.toString(transform)
  }

  const filteredProps = Object.fromEntries(Object.entries(props).filter(([k]) => k !== 'getData'))

  return <DragActivatorContext.Provider value={{ setActivatorNodeRef, attributes, listeners }}>
    <tr {...filteredProps} ref={setNodeRef} style={style}>
      {props.children}
    </tr>
  </DragActivatorContext.Provider>
}

const DragHandle = () => {
  const { setActivatorNodeRef, attributes, listeners } = useContext(DragActivatorContext)

  return <span ref={setActivatorNodeRef} style={{ fontSize: 18, cursor: 'move' }} {...attributes} {...listeners}>
    <FontAwesomeIcon icon={faGripDotsVertical} />
  </span>
}
